import React, { useContext } from 'react';
import { Box, Button, Text } from 'grommet';
import { Facebook, Next } from 'grommet-icons';

import { analytics } from '@services/analytics';

import FacebookDestinationPickerV3 from 'src/features/StreamingConfig/FacebookDestinationPickerV3';
import SHDCallout from '@ui/Callout/SHDCallout';
import SHDText from '@ui/Text/SHDText';
import { FACEBOOK_HOW_TO_SET_UP_STREAMING } from '@constants/streamingHelpTexts';
import { dtos } from '@shd/jslib/models';
import { useFacebookConnection } from './useFacebookConnection';
import { FacebookSettingsModalV2 } from '@features/Socials/Facebook/FacebookSettingsModalV2';
import HowToAndErrorModal from '@features/Socials/HowToAndErrorStreamingModal';
import { LiveStreamSettingsModal } from '@features/Socials/LiveStreamSettingsModal';
import { TeamContext, UserContext } from '@services/Session';

interface Props {
  truncateLength?: number | null;
}

const FacebookButton: React.FC<Props> = ({ truncateLength = null }) => {
  const teamStore = useContext(TeamContext);
  const userStore = useContext(UserContext);

  const {
    loading,
    error,
    publishFB,
    fbScopes,
    showIntroModal,
    showHowToSetUpModal,
    showLinkingModal,
    stepDestinationTitle,
    destinationGoBack,
    setShowIntroModal,
    setShowHowToSetUpModal,
    setShowLinkingModal,
    setStepDestinationTitle,
    setDestinationGoBack,
    onLinkFacebookClick,
    onClose,
    responseFacebook,
    setPublishFB,
  } = useFacebookConnection(teamStore.id);

  const formatTeamName = (teamName: string) =>
    truncateLength ? teamName.slice(0, truncateLength) + '...' : teamName;

  return (
    <Box>
      <Box skeleton={loading}>
        <SHDCallout round="medium">
          <Button
            onClick={
              publishFB
                ? () =>
                    window.open(
                      `https://www.facebook.com/${publishFB.destNodeId}`,
                      '_blank'
                    )
                : onLinkFacebookClick
            }
            style={{ width: '100%' }}
          >
            <Box direction="row" align="center" justify="between" pad="xsmall">
              <Box
                width="48px"
                height="48px"
                round="full"
                align="center"
                justify="center"
                style={{
                  backgroundColor: publishFB ? '#1877F2' : '#B6B6B6',
                }}
                flex={{ shrink: 0, grow: 0 }}
              >
                <Facebook size="24px" color={publishFB ? 'white' : '#FFFFFF'} />
              </Box>
              <Box flex style={{ marginLeft: '16px' }}>
                <SHDText style="subhead-semibold">Facebook</SHDText>
                <SHDText
                  style="footnote"
                  color={publishFB ? '#7C7C7C' : 'gray-500'}
                >
                  {publishFB
                    ? formatTeamName(publishFB.destNodeName || '')
                    : 'Link account'}
                </SHDText>
              </Box>
              <Box flex={{ shrink: 0, grow: 0 }}>
                <Next
                  size="14px"
                  color={publishFB ? 'black-alpha-30' : 'black'}
                />
              </Box>
            </Box>
          </Button>
        </SHDCallout>
      </Box>

      {showIntroModal && (
        <FacebookSettingsModalV2
          onLinkFacebookClick={onLinkFacebookClick}
          onCloseIntroModal={() => setShowIntroModal(false)}
          setShowLinkingModal={setShowLinkingModal}
          setShowHowToSetUpModal={() => {
            setShowHowToSetUpModal(true);
            analytics.track('Destination Path Choice Selected', {
              pathSelected: 'No',
              destinationType: 'Facebook',
              teamId: teamStore.id,
            });
          }}
          fbScopes={fbScopes}
          teamId={teamStore.id}
          responseFacebook={responseFacebook}
        />
      )}
      {showHowToSetUpModal && (
        <HowToAndErrorModal
          headerText="LINK FACEBOOK"
          title="How to set up Facebook livestreaming"
          subtitle=""
          onClose={() => setShowHowToSetUpModal(false)}
          isError={false}
          htmlContent={FACEBOOK_HOW_TO_SET_UP_STREAMING}
        />
      )}
      {showLinkingModal && (
        <LiveStreamSettingsModal
          headerText="LINK FACEBOOK"
          title={stepDestinationTitle}
          onCloseModal={onClose}
          goBack={destinationGoBack}
        >
          <FacebookDestinationPickerV3
            setStepDestinationTitle={setStepDestinationTitle}
            setDestinationGoBack={setDestinationGoBack}
            teamId={teamStore.id}
            onClose={() => {
              onClose();
              setShowIntroModal(false);
            }}
            onLinked={(p: dtos.PublishFb) => {
              (setPublishFB(p),
                analytics.track('Stream Destination Linked', {
                  userId: userStore.user._id,
                  teamId: teamStore.id,
                  sport: teamStore.sport,
                  destinationType: 'Facebook',
                  destinationName: p?.destNodeName,
                }));
            }}
          />
        </LiveStreamSettingsModal>
      )}
      {error && <Text color="status-error">{error}</Text>}
    </Box>
  );
};

export default FacebookButton;
